import { useTheme } from "@chakra-ui/react";
import { ReactElement, useState, useEffect } from "react";

function formatSeconds(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
}

export function CountDownTimerBorder(
  props: Readonly<{
    isEnable: boolean;
    isExpired: boolean;
    setSeconds: number;
    updateInterval?: number;
    onCountDownComplete?: () => void;
    children?: ReactElement | ReactElement[];
  }>
): ReactElement {
  // 初期設定
  const [progress, setProgress] = useState(0);
  const [lastProgress, setLastProgress] = useState(0);
  const [countDownCompletedEvent, setCountDownCompletedEvent] = useState(false);
  const [formattedRemainingTime, setFormattedRemainingTime] = useState(
    formatSeconds(props.setSeconds)
  );
  const [, setLastFormattedRemainingTime] = useState(
    formatSeconds(props.setSeconds)
  );
  const updateInterval = props.updateInterval || 100;
  const theme = useTheme();

  useEffect(() => {
    if (countDownCompletedEvent && props.onCountDownComplete) {
      props.onCountDownComplete();
    }
  }, [countDownCompletedEvent, props]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const additional_progress =
      (updateInterval / 1000 / props.setSeconds) * 100;
    if (props.isExpired) {
      setLastProgress(100);
      // setLastFormattedRemainingTimeの呼び出しは今回は不要なのでスキップ
    } else if (!props.isEnable) {
      setLastProgress(progress);
      setLastFormattedRemainingTime(formattedRemainingTime);
    } else if (interval === null && props.isEnable) {
      interval = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress: number =
            prevProgress < 100 ? prevProgress + additional_progress : 100;
          const timeProgress = (newProgress / 100) * props.setSeconds;
          const remainingTime = props.setSeconds - timeProgress;
          setFormattedRemainingTime(formatSeconds(remainingTime));
          if (remainingTime <= 0 && !countDownCompletedEvent && interval) {
            setCountDownCompletedEvent(true);
            clearInterval(interval);
          }
          return newProgress;
        });
      }, updateInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isEnable, props.setSeconds, updateInterval]);

  return (
    <ProgressBorder
      progress={props.isEnable ? progress : lastProgress}
      // size="200px"
      rounded="16px"
      borderWidth="6px"
      activeColor={theme.colors.primary["theme_lv1"]}
      inactiveColor={theme.colors.primary["theme_lv3"]}
      startAngle={0}
    >
      {props.children}
    </ProgressBorder>
  );
}

/**
 * 進捗ボーダー表示
 */
function ProgressBorder(
  props: Readonly<{
    progress: number;
    rounded?: string;
    borderWidth?: string;
    activeColor?: string;
    inactiveColor?: string;
    startAngle?: number;
    children?: ReactElement | ReactElement[];
  }>
): ReactElement {
  const {
    progress,
    rounded = "16px",
    borderWidth = "8px",
    activeColor = "#ef4444",
    inactiveColor = "#e5e7eb",
    startAngle = 0,
  } = props;
  const progressLUT = [
    0, 1.346973765, 2.693742379, 4.039895603, 5.385023387, 6.728715992,
    8.070564115, 9.410159016, 10.74709264, 12.08095775, 13.41134803,
    14.73785824, 16.0600843, 17.37762346, 18.69007437, 19.99703726, 21.29811401,
    22.59290829, 23.88102571, 25.16207389, 26.4356626, 27.70140391, 28.95891225,
    30.20780458, 31.44770047, 32.67822223, 33.89899504, 35.10964704,
    36.30980945, 37.4991167, 38.6772065, 39.84372, 40.99830186, 42.1406004,
    43.27026765, 44.38695951, 45.49033582, 46.58006049, 47.65580157,
    48.71723138, 49.7640266, 50.79586837, 51.81244237, 52.81343896, 53.79855321,
    54.76748505, 55.71993933, 56.65562593, 57.57425983, 58.4755612, 59.3592555,
    60.22507355, 61.0727516, 61.90203146, 62.71266051, 63.50439182, 64.27698423,
    65.0302024, 65.76381689, 66.47760424, 67.17134701, 67.87125511, 68.57810899,
    69.29169334, 70.01179079, 70.73818199, 71.47064567, 72.20895873,
    72.95289627, 73.70223166, 74.45673667, 75.21618145, 75.98033468,
    76.74896359, 77.52183404, 78.29871061, 79.07935666, 79.86353439,
    80.65100494, 81.44152843, 82.23486407, 83.03077019, 83.82900436,
    84.62932343, 85.4314836, 86.23524054, 87.04034942, 87.84656498, 88.65364165,
    89.46133358, 90.26939475, 91.07757901, 91.88564018, 92.69333212,
    93.50040879, 94.30662435, 95.11173322, 95.91549016, 96.71765034, 97.5179694,
    98.31620357, 99.1121097, 99.90544533, 100.6959688, 101.4834394, 102.2676171,
    103.0482632, 103.8251397, 104.5980102, 105.3666391, 106.1307923,
    106.8902371, 107.6447421, 108.3940775, 109.138015, 109.8763281, 110.6087918,
    111.335183, 112.0552804, 112.7688648, 113.4757187, 114.1756268, 114.8693695,
    115.5831569, 116.3167714, 117.0699895, 117.8425819, 118.6343133,
    119.4449423, 120.2742222, 121.1219002, 121.9877183, 122.8714126,
    123.7727139, 124.6913478, 125.6270344, 126.5794887, 127.5484206,
    128.5335348, 129.5345314, 130.5511054, 131.5829472, 132.6297424,
    133.6911722, 134.7669133, 135.8566379, 136.9600143, 138.0767061,
    139.2063734, 140.3486719, 141.5032538, 142.6697673, 143.8478571,
    145.0371643, 146.2373267, 147.4479787, 148.6687515, 149.8992733,
    151.1391692, 152.3880615, 153.6455699, 154.9113112, 156.1848999,
    157.4659481, 158.7540655, 160.0488598, 161.3499365, 162.6568994,
    163.9693503, 165.2868895, 166.6091155, 167.9356257, 169.266016, 170.5998811,
    171.9368147, 173.2764097, 174.6182578, 175.9619504, 177.3070782,
    178.6532314, 180, 181.3469738, 182.6937424, 184.0398956, 185.3850234,
    186.728716, 188.0705641, 189.410159, 190.7470926, 192.0809578, 193.411348,
    194.7378582, 196.0600843, 197.3776235, 198.6900744, 199.9970373, 201.298114,
    202.5929083, 203.8810257, 205.1620739, 206.4356626, 207.7014039,
    208.9589123, 210.2078046, 211.4477005, 212.6782222, 213.898995, 215.109647,
    216.3098095, 217.4991167, 218.6772065, 219.84372, 220.9983019, 222.1406004,
    223.2702677, 224.3869595, 225.4903358, 226.5800605, 227.6558016,
    228.7172314, 229.7640266, 230.7958684, 231.8124424, 232.813439, 233.7985532,
    234.767485, 235.7199393, 236.6556259, 237.5742598, 238.4755612, 239.3592555,
    240.2250735, 241.0727516, 241.9020315, 242.7126605, 243.5043918,
    244.2769842, 245.0302024, 245.7638169, 246.4776042, 247.171347, 247.8712551,
    248.578109, 249.2916933, 250.0117908, 250.738182, 251.4706457, 252.2089587,
    252.9528963, 253.7022317, 254.4567367, 255.2161815, 255.9803347,
    256.7489636, 257.521834, 258.2987106, 259.0793567, 259.8635344, 260.6510049,
    261.4415284, 262.2348641, 263.0307702, 263.8290044, 264.6293234,
    265.4314836, 266.2352405, 267.0403494, 267.846565, 268.6536416, 269.4613336,
    270.2693948, 271.077579, 271.8856402, 272.6933321, 273.5004088, 274.3066243,
    275.1117332, 275.9154902, 276.7176503, 277.5179694, 278.3162036,
    279.1121097, 279.9054453, 280.6959688, 281.4834394, 282.2676171,
    283.0482632, 283.8251397, 284.5980102, 285.3666391, 286.1307923,
    286.8902371, 287.6447421, 288.3940775, 289.138015, 289.8763281, 290.6087918,
    291.335183, 292.0552804, 292.7688648, 293.4757187, 294.1756268, 294.8693695,
    295.5831569, 296.3167714, 297.0699895, 297.8425819, 298.6343133,
    299.4449423, 300.2742222, 301.1219002, 301.9877183, 302.8714126,
    303.7727139, 304.6913478, 305.6270344, 306.5794887, 307.5484206,
    308.5335348, 309.5345314, 310.5511054, 311.5829472, 312.6297424,
    313.6911722, 314.7669133, 315.8566379, 316.9600143, 318.0767061,
    319.2063734, 320.3486719, 321.5032538, 322.6697673, 323.8478571,
    325.0371643, 326.2373267, 327.4479787, 328.6687515, 329.8992733,
    331.1391692, 332.3880615, 333.6455699, 334.9113112, 336.1848999,
    337.4659481, 338.7540655, 340.0488598, 341.3499365, 342.6568994,
    343.9693503, 345.2868895, 346.6091155, 347.9356257, 349.266016, 350.5998811,
    351.9368147, 353.2764097, 354.6182578, 355.9619504, 357.3070782,
    358.6532314, 360,
  ];
  const style = {
    width: "100%",
    height: "100%",
    borderRadius: rounded,
    padding: borderWidth,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    background: `conic-gradient(
      from ${startAngle}deg,
      ${activeColor} 0deg,
      ${activeColor} ${
      progressLUT[Math.round(Math.max((progress - 1) * 3.6, 0))]
    }deg,
      ${inactiveColor} ${progressLUT[Math.round(progress * 3.6)]}deg,
      ${inactiveColor} 360deg
    )`,
  };
  const innerStyle = {
    width: "100%",
    height: "100%",
    backgroundColor: "white", // 内側は白
    borderRadius: `calc(${rounded} - ${borderWidth})`, // 外側より小さい角丸
  };

  return (
    <div style={style}>
      <div style={innerStyle}>{props.children}</div>
    </div>
  );
}
